import { Component } from '@verndale/core';
import { gsap, Expo } from 'gsap';
import { v4 as uuidv4 } from 'uuid';
import { keyCode, debounce, camelize } from '../helpers';
import Dropdown from '../components/dropdown';

class Module extends Component {
  setupDefaults() {
    this.dom = {
      tabsContainer: this.el.querySelector('.solution-integrated-offerings-tab__tabs'),
      tabs: [...this.el.querySelectorAll('.solution-integrated-offerings-tab__tab')],
      panelsContainer: this.el.querySelector('.solution-integrated-offerings-tab__panels'),
      panels: [...this.el.querySelectorAll('.solution-integrated-offerings-tab__panel')]
    };

    this.buildNavigation();
    this.calculateSizes();
  }

  addListeners() {
    window.addEventListener(
      'resize',
      debounce(this.calculateSizes.bind(this), 300)
    );
    window.addEventListener('hashchange', this.handleCheckInitialAnchor.bind(this));

    this.dom.tabs.forEach((tab, index) => {
      tab.index = index;
      tab.addEventListener('click', this.handleTabClick.bind(this));
      tab.addEventListener('keydown', this.handleKeyDown.bind(this));
    });
    this.handleCheckInitialAnchor();
  }

  buildNavigation() {
    const options = this.dom.tabs
      .map((tab, index) => {
        const tabGuid = uuidv4();
        const panelGuid = uuidv4();
        const panel = this.dom.panels[index];
        const hrefVal = `#${camelize((tab.textContent).trim())}`

        tab.setAttribute('id', tabGuid);
        tab.setAttribute('aria-controls', panelGuid);
        tab.setAttribute('href', hrefVal);

        if (panel) {
          panel.setAttribute('id', panelGuid);
          panel.setAttribute('aria-labelledby', tabGuid);
          panel.setAttribute('data-link', hrefVal);
        }

        return `<option value="${panelGuid}">${(tab.textContent).trim()}</option>`;
      })
      .join('');

    const dropdownGuid = uuidv4();
    const dropdownContainer = document.createElement('div');
    dropdownContainer.classList.add('solution-integrated-offerings-tab__dropdown');

    const dropdownLabel = document.createElement('label');
    dropdownLabel.setAttribute('for', dropdownGuid);
    dropdownLabel.classList.add('label');
    dropdownLabel.innerHTML = this.dom.tabsContainer.getAttribute('aria-label');

    const dropdown = document.createElement('select');
    dropdown.id = dropdownGuid;
    dropdown.innerHTML = options;

    dropdownContainer.appendChild(dropdownLabel);
    dropdownContainer.appendChild(dropdown);
    this.dom.panelsContainer.parentNode.insertBefore(
      dropdownContainer,
      this.dom.panelsContainer
    );

    this.dom.dropdown = new Dropdown(dropdown);
    const customList = document.querySelectorAll('.solution-integrated-offerings-tab__dropdown .dropdown li');
    customList.forEach(item => {
      item.addEventListener('click', this.handleDropDownSelect.bind(this));
    });
    this.dom.dropdown.el.addEventListener('change', this.handleCustomDropDownSelect.bind(this));

    const listItems = document.querySelector('.dropdown__list').getElementsByTagName('li');
    const hrefVals = [];
    this.dom.tabs
      .map(tab => {
        hrefVals.push(`#${  camelize(tab.textContent)}`);
      });
    for (let i = 0; i < listItems.length; i++) {
      const li = listItems[i];
      const anchor = document.createElement('a');
      anchor.href = `${hrefVals[i]}`;
      anchor.textContent = li.textContent;
      li.innerHTML = '';
      li.appendChild(anchor);
    }
  }

  handleOptionChange(e) {
    e.preventDefault();
    const panelId = e.target.value;
    this.changePanel(panelId);
  }

  handleKeyDown(e) {
    const key = e.keyCode;
    const target = e.target;

    switch (key) {
      case keyCode.END:
        e.preventDefault();
        // Activate last tab
        this.dom.tabs[this.dom.tabs.length - 1].focus();
        this.changePanel(
          this.dom.tabs[this.dom.tabs.length - 1].getAttribute('aria-controls')
        );
        break;
      case keyCode.HOME:
        e.preventDefault();
        // Activate first tab
        this.dom.tabs[0].focus();
        this.changePanel(this.dom.tabs[0].getAttribute('aria-controls'));
        break;
      case keyCode.UP:
      case keyCode.LEFT:
        e.preventDefault();
        // Focus previous tab
        if (this.dom.tabs[target.index - 1]) {
          this.dom.tabs[target.index - 1].focus();
        } else {
          this.dom.tabs[this.dom.tabs.length - 1].focus();
        }
        break;
      case keyCode.DOWN:
      case keyCode.RIGHT:
        e.preventDefault();
        // Focus next tab
        if (this.dom.tabs[target.index + 1]) {
          this.dom.tabs[target.index + 1].focus();
        } else {
          this.dom.tabs[0].focus();
        }
        break;
    }
  }

  changePanel(panelId) {
    const currentTab = this.dom.tabs.find(
      tab => tab.getAttribute('aria-selected') === 'true'
    );
    const activeTab = this.dom.tabs.find(
      tab => tab.getAttribute('aria-controls') === panelId
    );
    const activeTabId = activeTab.getAttribute('aria-controls');
    const activeTabUrl = activeTab.getAttribute('data-link');

    if (currentTab !== activeTab) {
      activeTab.setAttribute('aria-selected', 'true');
      activeTab.setAttribute('tabindex', 0);

      currentTab.setAttribute('aria-selected', 'false');
      currentTab.setAttribute('tabindex', 1);

      currentTab.classList.remove('selected');
      activeTab.classList.add('selected');
      this.calculateSizes();

      this.dom.panels.forEach(panel => {
        if (panel.id === panelId) {
          gsap.to(panel, {
            autoAlpha: 1,
            ease: Expo.easeOut,
            duration: 0.4
          });
        } else {
          gsap.to(panel, {
            autoAlpha: 0,
            ease: Expo.easeOut,
            duration: 0.4
          });
        }
      });
      this.dom.panels.forEach(panel => {
        if (panel.id === activeTabId) {
          const panelIframe = panel.querySelector('iframe');
          if (panelIframe)
            panelIframe.setAttribute('src', panelIframe.getAttribute('src'));
        }
      });
    }
    this.handleScrollToSection(activeTabUrl, true);
  }

  calculateSizes() {
    const activePanelId = this.dom.tabs
      .find(tab => tab.getAttribute('aria-selected') === 'true')
      .getAttribute('aria-controls');

    const activePanelHeight = this.dom.panels.find(
      panel => panel.getAttribute('id') === activePanelId
    ).offsetHeight;

    this.dom.panelsContainer.style['height'] = `${activePanelHeight}px`;

    const activeTab = this.dom.tabs.find(
      tab => tab.getAttribute('aria-selected') === 'true'
    );
  }

  handleScrollToSection(tabUrl, push) {
    this.dom.tabsContainer.scrollIntoView({
      behavior: 'smooth',
      block: 'start'
    });

    if (push) history.pushState(null, null, tabUrl);
  }

  handleCheckInitialAnchor() {
    const { hash } = window.location;
    const tabToHighlight = this.dom.tabs.find(
      tab => tab.getAttribute('href') === hash
    );

    if (tabToHighlight) {
      const tabId = tabToHighlight.getAttribute('aria-controls');
      if (tabId) {
        const dropdownContainer = this.el.getElementsByClassName('dropdown')[0];
        const dropdown = dropdownContainer.getElementsByTagName('select')[0];
        dropdown.value = tabId;
        const changeEvent = new Event('change');
        dropdown.dispatchEvent(changeEvent);
        this.changePanel(tabId);

        if (hash) {
          this.jumpToTab(hash, tabId, true);
        }
      }
    }
  }

  handleTabClick(e) {
    e.preventDefault();
    const targetId = e.target.getAttribute('aria-controls');
    const targetURL = e.target.getAttribute('href');
    const tabToHighlight = this.dom.tabs.find(
      tab => tab.getAttribute('href') === targetURL
    );

    if (tabToHighlight) {
      const tabId = tabToHighlight.getAttribute('aria-controls');
      if (tabId) {
        this.changePanel(tabId);
      }
    }
    this.jumpToTab(targetURL, targetId, true);
  }

  handleDropDownSelect(e) {
    e.preventDefault();
    const targetId = e.target.getAttribute('data-value');
    const target = this.dom.tabs.find(tab => tab.getAttribute('aria-controls') === targetId);
    const targetURL = target.getAttribute('href');
    this.changePanel(targetId);
    this.jumpToTab(targetURL, targetId, true);
  }

  handleCustomDropDownSelect(e) {
    e.preventDefault();
    const selectedOption = e.target.selectedOptions[0]
    const targetId = selectedOption.getAttribute('value');
    const target = this.dom.tabs.find(tab => tab.getAttribute('aria-controls') === targetId);
    const targetURL = target.getAttribute('href');
    this.changePanel(targetId);
    this.jumpToTab(targetURL, targetId, true);
  }

  jumpToTab(targetURL, targetId, push) {
    const target = document.getElementById(targetId.replace('#', ''));
    if (target) {
      this.dom.tabsContainer.scrollIntoView({
        behavior: 'smooth',
        block: 'start'
      });
      document.querySelector('.solution-integrated-offerings-tab__dropdown').scrollIntoView({
        behavior: 'smooth',
        block: 'start'
      });

      if (push) {
        history.pushState(null, null, targetURL);
      }
    }
  }
}

export default Module;
